import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useCallback } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import CommonTable from "../../../common_components/CommonTable";
import TableRowExpanded from "../../../common_components/TableRowExpanded";
import PermissionBadges from "./PermissionBadges";
const isLeafModule = (module) => !module.sub_modules || module.sub_modules.length === 0;
const EditIconSvg = () => (_jsx("svg", { width: "20", height: "20", viewBox: "0 0 20 20", fill: "none", children: _jsx("path", { d: "M14.167 2.5A2.357 2.357 0 0 1 17.5 5.833L6.25 17.083l-4.583 1.25 1.25-4.583z", stroke: "#52525b", strokeWidth: "1.2", strokeLinecap: "round", strokeLinejoin: "round" }) }));
const DeleteIconSvg = () => (_jsx("svg", { width: "20", height: "20", viewBox: "0 0 20 20", fill: "none", children: _jsx("path", { d: "M2.5 5h1.667m0 0H17.5M4.167 5v11.667a1.667 1.667 0 0 0 1.666 1.667h8.334a1.667 1.667 0 0 0 1.666-1.667V5zm2.5 0V3.334a1.667 1.667 0 0 1 1.666-1.667h3.334a1.667 1.667 0 0 1 1.666 1.667V5m-5 4.167v5m3.334-5v5", stroke: "#535862", strokeWidth: "1.2", strokeLinecap: "round", strokeLinejoin: "round" }) }));
const ALL_COLUMNS = [
    { id: "module_name", label: "Module Name", width: "320px", align: "left" },
    { id: "key", label: "Key", width: "260px", align: "left" },
    { id: "url", label: "URL", width: "260px", align: "left" },
    { id: "order", label: "Order", width: "120px", align: "center", showColumnActions: false },
    { id: "actions", label: "Actions", width: "120px", align: "center", sortable: false, draggable: false, showColumnActions: false },
];
const renderPermissionExpanded = (module) => (_jsx(Box, { sx: {
        width: "100%",
        "& .table-row-expanded-side-panel": {
            width: "100%",
            maxWidth: "100%",
        },
    }, children: _jsx(TableRowExpanded, { row: module, renderSidePanel: () => (_jsx(Box, { sx: { width: "100%", p: 2 }, children: _jsx(PermissionBadges, { permissions: module.permissions, showDash: true, sx: { width: "100%" } }) })) }) }));
const NestedActionButtons = ({ row, onEdit, onDelete }) => (_jsxs(Box, { sx: { display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "2px", width: "100%" }, children: [_jsx(IconButton, { size: "small", onClick: (e) => { e.stopPropagation(); onEdit(row); }, children: _jsx(EditIconSvg, {}) }), _jsx(IconButton, { size: "small", onClick: (e) => { e.stopPropagation(); onDelete(row); }, children: _jsx(DeleteIconSvg, {}) })] }));
const NestedModulesTable = ({ modules, visibleColumnIds, onEdit, onDelete }) => {
    const rows = useMemo(() => (modules ?? []).map((m) => ({ ...m, id: m._id, children: Array.isArray(m.sub_modules) ? m.sub_modules.map((c) => ({ ...c, id: c._id, children: c.sub_modules })) : [] })), [modules]);
    const columns = useMemo(() => {
        if (!visibleColumnIds || visibleColumnIds.length === 0)
            return ALL_COLUMNS;
        return ALL_COLUMNS.filter((c) => visibleColumnIds.includes(c.id));
    }, [visibleColumnIds]);
    const renderCell = useCallback((row, column) => {
        switch (column.id) {
            case "module_name":
                return _jsx(Typography, { sx: { fontWeight: row.is_parent ? 600 : 400, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }, children: row.module_name });
            case "key":
                return _jsx(Typography, { sx: { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }, children: row.key });
            case "url":
                return _jsx(Typography, { sx: { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }, children: row.url || "-" });
            case "order":
                return _jsx(Typography, { children: row.order ?? "-" });
            case "actions":
                return _jsx(NestedActionButtons, { row: row, onEdit: onEdit, onDelete: onDelete });
            default:
                return null;
        }
    }, [onEdit, onDelete]);
    const renderExpandedContent = useCallback((row) => {
        const children = (row.sub_modules ?? []);
        if (!children.length) {
            return renderPermissionExpanded(row);
        }
        return (_jsx(NestedModulesTable, { modules: children, visibleColumnIds: visibleColumnIds, onEdit: onEdit, onDelete: onDelete }));
    }, [visibleColumnIds, onEdit, onDelete]);
    return (_jsx(Box, { sx: {
            p: 0,
            m: 0,
            width: "100%",
            "& .MuiTableHead-root": { display: "none" },
        }, children: _jsx(CommonTable, { columns: columns, rows: rows, renderCell: renderCell, renderExpandedContent: renderExpandedContent, expandTrigger: "icon", type: "modulelist-table nested-module-table", emptyMessage: "", expandCellClassName: "module-expand-cell" }) }));
};
const ModuleExpanded = ({ row, onEdit, onDelete, visibleColumnIds }) => {
    const children = (row.sub_modules ?? []);
    if (!children.length) {
        return renderPermissionExpanded(row);
    }
    return (_jsx(NestedModulesTable, { modules: children, visibleColumnIds: visibleColumnIds, onEdit: onEdit, onDelete: onDelete }));
};
export default ModuleExpanded;
